import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { pick } from 'lodash-es';
import {
  CommercialRoutePlan,
  GeneratedPlansMap,
  SuggestedPlansData,
  SuggestedSettingsEnum,
  SuggestedSettingsType,
  SurgeonRoutePlan,
} from '@lp/features/RoutePlanner/types';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import {
  generateFullRouteRequestBody,
  generatePartialRouteRequestBody,
  isAbortError,
  mapGroupsToPlanIds,
  showGenerationError,
} from '@lp/features/RoutePlanner/utils';
import { ResourceLogisticsStatus, ServiceType } from '@/api/apis/case';
import { PlanActionTeamKeyword, RouteTypeSettingsEnum, TeamResourcesByKeyword } from '@/api/apis/mission-planner';
import { useConvertSuggestedRoutes } from './useConvertSuggestedRoutes';
import { useSuggestedMutations } from './useSuggestedMutations';
import {
  INITIAL_SUGGESTED_ROUTES_STATE,
  SuggestedRoutesAction,
  suggestedRoutesReducer,
  SuggestedRoutesState,
} from './suggestedRoutesReducer';

const EMPTY_SUGGESTED_PLANS_DATA: SuggestedPlansData = {
  taskId: '',
  planType: RouteTypeSettingsEnum.Full,
  plans: {},
  forcedSurgeonId: '',
};

type AdditionalRouteFetchParams = {
  taskId: string;
  planType: RouteTypeSettingsEnum;
  plans: GeneratedPlansMap;
  isTmdxDriveCompatible: boolean;
  forcedSurgeonId?: string;
};

type AdditionalRouteFetchType = (params: AdditionalRouteFetchParams & { signal: AbortSignal }) => Promise<void>;

type UseSuggestedRoutesStateReturnType = SuggestedRoutesState & {
  sortedRoutePlans: { flightTime: number; driveTime: number; taskId: string; planIndex: number }[];
  fetchRoutes: (settings: SuggestedSettingsType[], isTmdxDriveCompatible: boolean) => Promise<SuggestedPlansData>;
  fetchAdditionalRoutes: (params: AdditionalRouteFetchParams) => Promise<void>;
  setGeneratedRoutesStatistics: ReturnType<typeof useSuggestedMutations>['setGeneratedRoutesStatistics'];
};

export const useSuggestedRoutesState = (): UseSuggestedRoutesStateReturnType => {
  const caseData = useCaseDetailsWithReviews();
  const { serviceType } = caseData;
  const { ClinicalSpecialist, OCSConsole } = useRouteResources();
  const [state, dispatch] = useReducer(suggestedRoutesReducer, INITIAL_SUGGESTED_ROUTES_STATE);
  const { isPlansLoaded, routePlans } = state;

  const generationRef = useRef<AbortController | null>(null);

  const startGeneration = useCallback(() => {
    generationRef.current?.abort();
    generationRef.current = new AbortController();

    return generationRef.current.signal;
  }, []);

  const currentGeneration = useCallback(() => {
    generationRef.current ??= new AbortController();

    return generationRef.current.signal;
  }, []);

  const dispatchFor = useCallback((signal: AbortSignal, action: SuggestedRoutesAction) => {
    if (signal.aborted) return;

    dispatch(action);
  }, []);

  useEffect(() => () => generationRef.current?.abort(), []);

  const teamResourcesByKeyword = useMemo<Pick<
    TeamResourcesByKeyword,
    PlanActionTeamKeyword.team | PlanActionTeamKeyword.teamWithoutOcs
  > | null>(() => {
    const activeOcsDeviceOnCase =
      OCSConsole.find((d) => d.logisticsStatus !== ResourceLogisticsStatus.OCSDone) || OCSConsole[0];
    const activeCSOnCase =
      ClinicalSpecialist.find((d) => d.logisticsStatus !== ResourceLogisticsStatus.Done) || ClinicalSpecialist[0];

    if (!activeOcsDeviceOnCase || !activeCSOnCase) return null;

    const ocsDevice = { id: activeOcsDeviceOnCase.resourceId };
    const specialist = pick(activeCSOnCase.data!, [
      'id',
      'firstName',
      'lastName',
      'profilePhoto',
      'organSpecializations',
    ]);

    return {
      [PlanActionTeamKeyword.team]: {
        ocs: ocsDevice,
        specialist,
      },
      [PlanActionTeamKeyword.teamWithoutOcs]: {
        specialist,
      },
    };
  }, [ClinicalSpecialist, OCSConsole]);

  const sortedRoutePlans = useMemo(() => {
    if (!isPlansLoaded) return [];

    return Object.entries(routePlans)
      .flatMap(([taskId, taskResult]) =>
        taskResult.routes.map((plan, planIndex) => ({
          flightTime: plan.metrics.totalFlightDuration,
          driveTime: plan.metrics.totalDriveDuration,
          taskId,
          planIndex,
        })),
      )
      .toSorted((a, b) => (a.flightTime || b.flightTime ? a.flightTime - b.flightTime : a.driveTime - b.driveTime));
  }, [isPlansLoaded, routePlans]);

  const {
    createSuggestedRoutesTask,
    createSurgeonTrips,
    createCommercialFlightsTrips,
    createPartialRoutesTask,
    createPartialCommercialFlightsTrips,
    setGeneratedRoutesStatistics,
  } = useSuggestedMutations();
  const {
    getSuggestedRoutes,
    getSuggestedSurgeonRoutes,
    getCommercialFlightsRoutes,
    getPartialRoutes,
    getPartialCommercialFlightsRoutes,
  } = useConvertSuggestedRoutes();

  const getMultiOrRoutes = useCallback(
    async (settings: SuggestedSettingsType[], isTmdxDriveCompatible: boolean, signal: AbortSignal) => {
      const {
        orTimesData,
        forcedSurgeonId = '',
        ...fullRouteRequestBody
      } = generateFullRouteRequestBody(settings, caseData);

      const taskId = await createSuggestedRoutesTask(fullRouteRequestBody);
      const { routes: routePlansList, warning } = await getSuggestedRoutes({
        planId: taskId,
        planType: fullRouteRequestBody.partialTripType,
        isTmdxDriveCompatible,
        signal,
      });

      const baseRoutePlans = orTimesData.reduce((acc, orTime, index) => {
        const routeList = routePlansList[index];

        if (!routeList?.length) return acc;

        return {
          ...acc,
          [orTime.id]: {
            id: orTime.id,
            index,
            orTimeUTC: orTime.orTimeUTC,
            orTimeDiffMs: orTime.orTimeDiffMs,
            routes: routeList,
          },
        };
      }, {} as GeneratedPlansMap);

      dispatchFor(signal, {
        type: 'plansLoaded',
        taskId,
        planType: fullRouteRequestBody.partialTripType,
        plans: baseRoutePlans,
        generatedDateTimeMs: Date.now(),
        warning,
      });

      return { taskId, planType: fullRouteRequestBody.partialTripType, plans: baseRoutePlans, forcedSurgeonId };
    },
    [caseData, createSuggestedRoutesTask, getSuggestedRoutes, dispatchFor],
  );

  const getPartialRouteData = useCallback(
    async (settings: SuggestedSettingsType[], isTmdxDriveCompatible: boolean, signal: AbortSignal) => {
      if (!teamResourcesByKeyword) return EMPTY_SUGGESTED_PLANS_DATA;
      const partialRequestBody = generatePartialRouteRequestBody(settings, caseData);

      const taskId = await createPartialRoutesTask(partialRequestBody);
      const { routes: routeList, warning } = await getPartialRoutes({
        planId: taskId,
        planType: partialRequestBody.partialTripType,
        teamResources: teamResourcesByKeyword,
        isTmdxDriveCompatible,
        signal,
      });

      const baseRoutePlans = !routeList?.length
        ? {}
        : {
            [taskId]: {
              id: taskId,
              index: 0,
              orTimeUTC: partialRequestBody.orTime ?? partialRequestBody.tripStartTime,
              orTimeDiffMs: 0,
              routes: routeList,
            },
          };

      dispatchFor(signal, {
        type: 'plansLoaded',
        taskId,
        planType: partialRequestBody.partialTripType,
        plans: baseRoutePlans,
        generatedDateTimeMs: Date.now(),
        warning,
      });

      return { taskId, planType: partialRequestBody.partialTripType, plans: baseRoutePlans };
    },
    [caseData, createPartialRoutesTask, teamResourcesByKeyword, getPartialRoutes, dispatchFor],
  );

  const fetchRoutes = useCallback(
    async (settings: SuggestedSettingsType[], isTmdxDriveCompatible: boolean) => {
      const signal = startGeneration();

      dispatch({ type: 'reset' });

      const routeTypeSettings = settings.find((s) => s.type === SuggestedSettingsEnum.RouteType);

      if (!routeTypeSettings) {
        return EMPTY_SUGGESTED_PLANS_DATA;
      }

      try {
        switch (routeTypeSettings.value) {
          case RouteTypeSettingsEnum.Custom_DH_TXC_Hub:
          case RouteTypeSettingsEnum.Custom_TXC_Hub:
            return await getPartialRouteData(settings, isTmdxDriveCompatible, signal);
          default:
            return await getMultiOrRoutes(settings, isTmdxDriveCompatible, signal);
        }
      } catch (e) {
        if (isAbortError(e)) return { ...EMPTY_SUGGESTED_PLANS_DATA, aborted: true };

        showGenerationError(e);
      }

      return EMPTY_SUGGESTED_PLANS_DATA;
    },
    [getMultiOrRoutes, getPartialRouteData, startGeneration],
  );

  const onSurgeonPlansProgress = useCallback(
    (plans: GeneratedPlansMap, signal: AbortSignal) => (groups: Record<number, (SurgeonRoutePlan | null)[]>) => {
      dispatchFor(signal, { type: 'surgeonPlansUpdated', plans: mapGroupsToPlanIds(plans, groups) });
    },
    [dispatchFor],
  );

  const onCommercialPlansProgress = useCallback(
    (plans: GeneratedPlansMap, signal: AbortSignal) => (groups: Record<number, CommercialRoutePlan[]>) => {
      dispatchFor(signal, { type: 'commercialPlansUpdated', plans: mapGroupsToPlanIds(plans, groups) });
    },
    [dispatchFor],
  );

  const getSurgeonTrips: AdditionalRouteFetchType = useCallback(
    async ({ taskId, planType, plans, isTmdxDriveCompatible, forcedSurgeonId = '', signal }) => {
      let warning: string | null = null;

      try {
        if ([RouteTypeSettingsEnum.Full, RouteTypeSettingsEnum.Hub_DH].includes(planType)) {
          await createSurgeonTrips(taskId, forcedSurgeonId);
          warning = await getSuggestedSurgeonRoutes({
            planId: taskId,
            isTmdxDriveCompatible,
            onProgress: onSurgeonPlansProgress(plans, signal),
            signal,
          });
        }
      } finally {
        dispatchFor(signal, { type: 'surgeonPlansSettled', warning });
      }
    },
    [createSurgeonTrips, getSuggestedSurgeonRoutes, onSurgeonPlansProgress, dispatchFor],
  );

  const getCommercialTrips: AdditionalRouteFetchType = useCallback(
    async ({ taskId, planType, plans, isTmdxDriveCompatible, signal }) => {
      try {
        if (planType === RouteTypeSettingsEnum.Full) {
          await createCommercialFlightsTrips(taskId);
          await getCommercialFlightsRoutes({
            planId: taskId,
            basePlans: plans,
            isTmdxDriveCompatible,
            onProgress: onCommercialPlansProgress(plans, signal),
            signal,
          });
        }
      } finally {
        dispatchFor(signal, { type: 'commercialPlansSettled' });
      }
    },
    [createCommercialFlightsTrips, getCommercialFlightsRoutes, onCommercialPlansProgress, dispatchFor],
  );

  const getPartialCommercialTrips: AdditionalRouteFetchType = useCallback(
    async ({ taskId, planType, plans, isTmdxDriveCompatible, signal }) => {
      try {
        if ([RouteTypeSettingsEnum.Custom_DH_TXC_Hub, RouteTypeSettingsEnum.Custom_TXC_Hub].includes(planType)) {
          await createPartialCommercialFlightsTrips(taskId);
          await getPartialCommercialFlightsRoutes({
            planId: taskId,
            basePlans: plans,
            isTmdxDriveCompatible,
            onProgress: onCommercialPlansProgress(plans, signal),
            signal,
          });
        }
      } finally {
        dispatchFor(signal, { type: 'commercialPlansSettled' });
      }
    },
    [createPartialCommercialFlightsTrips, getPartialCommercialFlightsRoutes, onCommercialPlansProgress, dispatchFor],
  );

  const fetchAdditionalRoutes = useCallback(
    async (fetchParams: AdditionalRouteFetchParams) => {
      const { planType } = fetchParams;
      const signal = currentGeneration();
      const params = { ...fetchParams, signal };

      try {
        switch (planType) {
          case RouteTypeSettingsEnum.Full:
            if (serviceType !== ServiceType.NopWithTmdxSurgeon) {
              dispatchFor(signal, { type: 'surgeonPlansSettled' });
            }

            await Promise.all([
              serviceType === ServiceType.NopWithTmdxSurgeon ? getSurgeonTrips(params) : Promise.resolve(),
              getCommercialTrips(params),
            ]);
            break;
          case RouteTypeSettingsEnum.Hub_DH:
            dispatchFor(signal, { type: 'commercialPlansSettled' });

            if (serviceType === ServiceType.NopWithTmdxSurgeon) {
              await getSurgeonTrips(params);
            } else {
              dispatchFor(signal, { type: 'surgeonPlansSettled' });
            }
            break;
          case RouteTypeSettingsEnum.Custom_DH_TXC_Hub:
          case RouteTypeSettingsEnum.Custom_TXC_Hub:
            dispatchFor(signal, { type: 'surgeonPlansSettled' });
            await getPartialCommercialTrips(params);
            break;
          default:
            break;
        }
      } catch (e) {
        showGenerationError(e);
      }
    },
    [getCommercialTrips, getPartialCommercialTrips, getSurgeonTrips, serviceType, currentGeneration, dispatchFor],
  );

  return {
    ...state,
    sortedRoutePlans,
    fetchRoutes,
    fetchAdditionalRoutes,
    setGeneratedRoutesStatistics,
  };
};
